import { AwaitContainer, Button, CardContent, CardHeader } from '@ibsys/kit';
import { ArrowLeft, PanelLeftIcon } from 'lucide-react';
import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogPanelCard,
  DialogPanelToggle,
  isTextConversation,
  useConversation,
  useConversationMessages,
  useIsDialogPanelShown,
} from '@/entities/conversations';
import { ChatFontSizeButton } from '@/entities/settings';
import { cn } from '@/shared/lib';
import { IconButton } from '@/shared/ui';
import { DialogTextFeed } from './ui/DialogTextFeed';
import { DialogTextHeader } from './ui/DialogTextHeader';

type Props = {
  dialogId: number;
  /** Секция сворачивается переключателем боковой панели */
  isCollapsible?: boolean;
  /** Панель атрибутов рядом с перепиской открыта */
  isAttributesPanelOpen?: boolean;
  /** Открыть или скрыть панель атрибутов; без него кнопки панели атрибутов нет */
  onToggleAttributesPanel?: () => void;
  /**
   * Возврат к списку. Обычно его кнопка — в панели атрибутов; пока панель скрыта, кнопка
   * переезжает в шапку переписки, иначе из карточки было бы не уйти.
   */
  onBack?: () => void;
};

/**
 * Текстовая коммуникация вместо расшифровки и записи: шапка с датами и тематикой обращения
 * и переписка. Блока записи у неё нет.
 */
export const DialogTextSection: FC<Props> = memo(
  ({ dialogId, isCollapsible = true, isAttributesPanelOpen, onToggleAttributesPanel, onBack }) => {
    const { t } = useTranslation(['dialogs', 'common']);
    const { data: conversation, isLoading: isLoadingConversation } = useConversation(dialogId);
    const { data: messages, isLoading: isLoadingMessages } = useConversationMessages(
      dialogId,
      isTextConversation(conversation),
    );

    const showDialog = useIsDialogPanelShown(isCollapsible);

    return (
      <DialogPanelCard isLoading={isLoadingConversation}>
        {/* Шапка не уступает высоту переписке: иначе перенесённые даты и тематика легли бы поверх неё */}
        <CardHeader
          className={cn(!showDialog && 'px-2', 'relative flex min-h-12 shrink-0 items-start gap-4 py-2 md:pt-3')}
        >
          {isCollapsible && <DialogPanelToggle />}
          {showDialog && onBack && (
            <Button variant="link" className="shrink-0" icon={<ArrowLeft />} onClick={onBack}>
              {t('common:back')}
            </Button>
          )}
          {showDialog && (
            <DialogTextHeader
              conversation={conversation}
              action={
                <>
                  <ChatFontSizeButton />
                  {onToggleAttributesPanel && (
                    <IconButton
                      size="icon"
                      variant={isAttributesPanelOpen ? 'secondary' : 'ghost'}
                      label={
                        isAttributesPanelOpen
                          ? t('dialogs:textFeed.hideAttributes')
                          : t('dialogs:textFeed.showAttributes')
                      }
                      icon={<PanelLeftIcon className="size-4" />}
                      aria-pressed={isAttributesPanelOpen}
                      onClick={onToggleAttributesPanel}
                    />
                  )}
                </>
              }
            />
          )}
        </CardHeader>
        {showDialog && (
          <CardContent className="flex h-full min-h-0 flex-col overflow-hidden px-0">
            <AwaitContainer isLoading={isLoadingMessages} className="min-h-0 flex-1">
              <DialogTextFeed dialogId={dialogId} messages={messages ?? []} />
            </AwaitContainer>
          </CardContent>
        )}
      </DialogPanelCard>
    );
  },
);
